<template>
  <v-card flat color="transparent" class="bookmark-item--headline d-flex align-start">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="mx-3 mt-0"
      :checked="isSelected"
      @click="toggleSelect"
    />
    <div class="full-width">
      <div class="text-neutral400 d-flex align-center mb-2 text-caption">
        <BookmarkSource :bookmark="bookmark" />
        <span class="mx-1">&middot;</span>
        <BookmarkDate :value="bookmark.createdAt" />
      </div>

      <v-card-title
        class="bookmark-headline pa-0 mb-3 text-body-1 font-weight-semibold text-neutral900"
        tag="a" :href="bookmark.brandedUrl" target="_blank" rel="noopener"
        @click="onLinkClick"
      >
        <v-progress-circular
          v-if="bookmark.isLoading && !bookmark.hasSufficientData"
          indeterminate size="16" color="blue400" width="3"
          class="mr-2"
        />
        <span v-text="bookmark.title ?? bookmark.domain" />
      </v-card-title>
      <BookmarkTags v-if="bookmark.tags.length" class="mb-3" :tags="bookmark.tags" @tag-selected="onTagSelected" />

      <BookmarkActions
        :bookmark="bookmark" :user-id="userId"
        :public-mode="publicMode"
        :membership="membership"
        class="text-text"
        @favorite="$emit('favorite')"
        @save="$emit('save')"
        @add-to-collection="$emit('add-to-collection')"
        @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
        @comments="$emit('comments')"
        @like="$emit('like')"
        @likes="$emit('likes')"
        @more="$emit('more')"
        @pin-edit="$emit('pin-edit')"
      />
    </div>
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import BookmarkSource from './BookmarkSource'
import BookmarkActions from './BookmarkActions'
import BookmarkDate from './BookmarkDate'
import BookmarkTags from './BookmarkTags'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'

const props = defineProps({
  // Accepts both Bookmark and CollectionItem (when resourceType is 'bookmark')
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'more',
  'pin-edit',
])

const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => {
  return isItemSelected(props.bookmark.id)
})

function onLinkClick(event) {
  if (isBulkActionsActive.value) {
    event.preventDefault()
    toggleSelect()
  }
}

function toggleSelect() {
  emit('toggle-select', props.bookmark)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.bookmark-headline {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
}

.bookmark-description {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  overflow: hidden;
}
</style>
